import { useCallback, useEffect, useMemo, useRef } from "react";
import { type useZero } from "@rocicorp/zero/react";
import { toast } from "sonner";
import { mutators } from "@/lib/mutators";

interface PinRow {
  id: string;
  message_id: string;
}

interface UseChannelPinsArgs<MessageRow extends { id: string }> {
  channelId: string | null;
  user: { id: string } | null;
  messageById: Map<string, MessageRow>;
  zero: ReturnType<typeof useZero>;
  /**
   * Pins for the current channel, sourced from
   * `queries.channels.byIdComposite(channelId).pins`. The hook is now
   * presentational over this prop — it no longer opens its own pins
   * subscription. Folded into the channel composite to cut per-channel
   * hub count from 3 → 1.
   */
  channelPinsRaw: ReadonlyArray<PinRow> | undefined;
}

/**
 * Owns pinned-message derivations for `MessageArea`.
 *
 * Receives `channelPinsRaw` from the parent's `channels.byIdComposite`
 * subscription and exposes the three derived shapes the rest of the
 * component needs:
 *
 * - `pinnedMessageIds` — used by `MessageListView` to highlight rows.
 * - `pinByMessageId` — used by `handlePin` to look up the existing pin
 *   when toggling.
 * - `visibleChannelPins` — pins whose underlying message is loaded; the
 *   header counter and the panel both render from this so an orphan pin
 *   (message deleted, not in the loaded slice) doesn't make the counter
 *   disagree with the list.
 *
 * `handlePin` toggles: insert if missing, delete if present. Errors
 * surface via a sonner toast.
 */
export function useChannelPins<MessageRow extends { id: string }>({
  channelId,
  user,
  messageById,
  zero,
  channelPinsRaw,
}: UseChannelPinsArgs<MessageRow>) {
  const channelPins = useMemo(() => channelPinsRaw ?? [], [channelPinsRaw]);

  const pinnedMessageIds = useMemo(() => {
    const set = new Set<string>();
    for (const pin of channelPins) {
      set.add(pin.message_id);
    }
    return set;
  }, [channelPins]);
  const pinByMessageId = useMemo(() => {
    const map = new Map<string, (typeof channelPins)[number]>();
    for (const pin of channelPins) {
      map.set(pin.message_id, pin);
    }
    return map;
  }, [channelPins]);

  // Pins whose underlying message is actually loaded. The header counter
  // and the pinned-messages panel both render from this so orphan pins
  // (message deleted, not yet loaded) can't make the counter disagree
  // with the list.
  const visibleChannelPins = useMemo(
    () => channelPins.filter((p) => messageById.has(p.message_id)),
    [channelPins, messageById],
  );

  // Read `pinByMessageId` through a ref inside the callback so
  // `handlePin`'s identity stops depending on the Map. The Map is
  // rebuilt every time `channelPinsRaw` (= composite?.pins) flips
  // identity — which happens on every byIdComposite IVM emission, even
  // when pin content is unchanged. Without this ref, `handlePin` ⇒
  // `onPin` prop on every MessageRow flips on each emission and breaks
  // `MessageRow.memo` for the entire visible list (300+ rows). The ref
  // sees the latest Map at click time without forcing the callback to
  // re-create.
  const pinByMessageIdRef = useRef(pinByMessageId);
  useEffect(() => {
    pinByMessageIdRef.current = pinByMessageId;
  }, [pinByMessageId]);

  const handlePin = useCallback(
    (messageId: string) => {
      if (!user || !channelId) return;
      const existingPin = pinByMessageIdRef.current.get(messageId);
      try {
        if (existingPin) {
          zero.mutate(mutators.pins.delete({ id: existingPin.id }));
          toast.success("Message unpinned");
        } else {
          zero.mutate(
            mutators.pins.insert({
              id: crypto.randomUUID(),
              channel_id: channelId,
              message_id: messageId,
              pinned_by: user.id,
              pinned_at: Date.now(),
            }),
          );
          toast.success("Message pinned");
        }
      } catch (err) {
        console.error("[message-area] pin/unpin failed:", err);
        toast.error("Failed to pin/unpin message");
      }
    },
    [user, channelId, zero],
  );

  return {
    channelPins,
    pinnedMessageIds,
    pinByMessageId,
    visibleChannelPins,
    handlePin,
  };
}
